import { Award, Users, Target, Lightbulb, ArrowRight } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CertificatesSection from "../components/CertificatesSection";
import SparkleButton from "../components/SparkleButton";
import { aboutContent, agencyInfo } from "../mock";
import { Link } from "react-router-dom";

const AboutPage = () => {
  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            backgroundSize: '60px 60px'
          }}></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="inline-block animate-fade-in">
              <Badge className="px-4 py-2 bg-gradient-to-r from-blue-400/20 to-blue-600/20 border border-blue-400/40 text-blue-300 text-sm font-bold">
                👋 About Us 👋
              </Badge>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold animate-slide-in-left">
              Meet the <span className="bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 bg-clip-text text-transparent">Creative Force</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed animate-slide-in-right">
              Driven by passion, powered by innovation, dedicated to your success.
            </p>
            <div className="golden-accent-line"></div>
          </div>
        </div>
      </section>

      {/* Founder Bio Section */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-in-left">
              <div className="relative">
                <div className="absolute inset-0 rounded-3xl blur-2xl opacity-40 animate-pulse" style={{ background: 'radial-gradient(circle, #e6c200 0%, transparent 70%)' }}></div>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl golden-border">
                  <img
                    src="https://customer-assets.emergentagent.com/job_ambitions-potency/artifacts/84wbcfvz_IMG-20220607-WA0054.jpg"
                    alt="Karthipan Karunamoorthy - Founder"
                    className="relative w-full h-[600px] object-cover hover-scale"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <h3 className="text-3xl font-bold mb-2">{agencyInfo.owner}</h3>
                    <p className="text-lg text-yellow-400">Founder & Creative Director</p>
                  </div>
                </div>
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl shadow-xl shadow-yellow-500/50 p-6 animate-float">
                  <div className="text-center">
                    <p className="text-4xl font-bold text-black">{aboutContent.experience}</p>
                    <p className="text-sm text-black font-bold">Experience</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6 animate-slide-in-right">
              <div>
                <span className="text-yellow-400 font-bold text-xs uppercase tracking-[0.2em]">About Me</span>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 mt-2">
                  Hi, I'm <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">{agencyInfo.owner}</span>
                </h2>
                <p className="text-lg text-gray-300 leading-relaxed">
                  {aboutContent.bio}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-xl hover-lift card-shine">
                  <p className="text-3xl font-bold text-blue-600 animate-pulse">{aboutContent.projectsCompleted}</p>
                  <p className="text-sm text-gray-600 font-medium mt-1">Projects</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-xl hover-lift card-shine">
                  <p className="text-3xl font-bold text-orange-600 animate-pulse">{aboutContent.happyClients}</p>
                  <p className="text-sm text-gray-600 font-medium mt-1">Clients</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-xl hover-lift card-shine">
                  <p className="text-3xl font-bold text-green-600 animate-pulse">{aboutContent.awards}</p>
                  <p className="text-sm text-gray-600 font-medium mt-1">Awards</p>
                </div>
              </div>

              <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white animate-glow">
                <Link to="/contact">
                  Let's Work Together <ArrowRight className="ml-2" size={18} />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16 space-y-4">
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Expertise</span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                Skills & <span className="gradient-text">Proficiency</span>
              </h2>
            </div>

            <div className="space-y-6">
              {aboutContent.skills.map((skill, index) => (
                <div
                  key={index}
                  className="animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">{skill.name}</span>
                    <span className="text-sm font-medium text-blue-600">{skill.level}%</span>
                  </div>
                  <Progress value={skill.level} className="h-3" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 hover:border-blue-400 hover-lift animate-slide-in-left">
              <CardContent className="p-8 space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-400 rounded-2xl flex items-center justify-center">
                  <Target className="text-white" size={32} />
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Our Mission</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  {aboutContent.mission}
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-orange-400 hover-lift animate-slide-in-right">
              <CardContent className="p-8 space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-400 rounded-2xl flex items-center justify-center">
                  <Lightbulb className="text-white" size={32} />
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Our Vision</h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  {aboutContent.vision}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-orange-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <span className="text-orange-500 font-semibold text-sm uppercase tracking-wider">Core Values</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              What We <span className="gradient-text">Believe In</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Award,
                title: "Excellence",
                description: "Delivering exceptional quality in every project"
              },
              {
                icon: Users,
                title: "Collaboration",
                description: "Working together to achieve shared goals"
              },
              {
                icon: Lightbulb,
                title: "Innovation",
                description: "Embracing creativity and new technologies"
              },
              {
                icon: Target,
                title: "Results",
                description: "Focused on measurable outcomes and success"
              }
            ].map((value, index) => {
              const Icon = value.icon;
              return (
                <Card
                  key={index}
                  className="border-2 hover:border-blue-400 hover-lift animate-scale-in text-center"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-orange-500 rounded-2xl">
                      <Icon className="text-white" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{value.title}</h3>
                    <p className="text-gray-600 text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-orange-500 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold">
              Let's Create Something Amazing
            </h2>
            <p className="text-xl text-blue-100">
              Ready to transform your digital presence? Get in touch today!
            </p>
            <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg">
              <Link to="/contact">Contact Us Now</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Certificates Section */}
      <CertificatesSection />

      <Footer />
    </div>
  );
};

export default AboutPage;